import { useEffect, useMemo, useState } from 'react'
import { ArrowDownRight, ArrowRight, ArrowUpRight, GitCompareArrows, Handshake, Plus, RotateCcw, Search, Settings2 } from 'lucide-react'
import { Link, navigate } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Checkbox } from '../admin/form.js'
import { Select } from '../admin/primitives.js'
import { SegmentedControl } from '../admin/pickers.js'
import { remplacerRecherche } from '../finance/adresse-ecran.js'
import { idDuCas } from '../soirees/rattachement-model.js'
import { useLecture } from './associations-api.js'
import { ADecider, NuitsDesEvenementsBloc } from './ADecider.js'
import { Chargement, Erreur, ModaleNouvelle, PastilleKind, PastilleStatut } from './AssociationsCommun.js'
import {
  CHEMIN_LISTE, CHEMIN_REGLAGES, COMPARER_MAX, COMPARER_MIN, FILTRES_DEFAUT, LIBELLES_KIND, VUE_A_DECIDER,
  aideEntrees, appliquerFiltres, basculerSelection, cheminComparer, cheminFiche, compte, detailADecider, filtresActifs, filtresDepuisUrl, filtresVersUrl,
  libelleSource, nombre, nuitCourte, nuitLongue, pourcent, saisonsDe, texteTendance,
  type FiltreStatut, type FiltresListe, type LigneAssociation, type ReponseListe, type TriListe,
} from './associations-model.js'

/**
 * EXPLOITATION › ASSOCIATIONS — LA LISTE (`/exploitation/associations`, 20/10/2026).
 *
 * Suivre chaque BDE ou association d'une soirée à l'autre : combien de
 * billets elle vend, combien de personnes entrent vraiment (des billets
 * scannés), si elle monte ou baisse, et quand est sa prochaine soirée.
 *
 * En tête, « À décider » — ce que Gestion ne tranche pas seul, dont les deux
 * listes déménagées de Réglages › Billetteries. `?vue=a-decider` (le lien
 * des notifications) y fait défiler la page.
 *
 * Les filtres sont dans l'adresse (`remplacerRecherche` : l'entrée
 * d'historique est remplacée, le Retour quitte la liste en un clic, et
 * depuis une fiche il la retrouve filtrée). La sélection « Comparer » ne
 * l'est pas : elle mène à sa propre adresse, partageable.
 */

export function AssociationsListePage() {
  const { etat, relire } = useLecture<ReponseListe>('/api/v1/associations?statut=toutes', 120_000)
  return (
    <main className="lc-page lc-page--management lc-stagger asso-page">
      <Trail />
      <SmartBack fallback="/exploitation" />
      {etat.phase === 'chargement' ? <><EnTete gerer={false} onNouvelle={() => undefined} /><Chargement libelle="Lecture des associations" /></>
        : etat.phase === 'erreur' ? <><EnTete gerer={false} onNouvelle={() => undefined} /><Erreur message={etat.message} onReessayer={relire} /></>
          : <Liste r={etat.data} />}
    </main>
  )
}

function EnTete({ gerer, onNouvelle, comparer }: { gerer: boolean; onNouvelle: () => void; comparer?: React.ReactNode }) {
  return (
    <header className="asso-hero">
      <div>
        <h1 className="lc-title"><Handshake size={26} aria-hidden="true" /> Associations</h1>
        <p className="lc-subtitle">
          Les BDE et associations qui organisent leurs soirées au club : billets vendus, entrées scannées, tendance et
          prochaine soirée, de l’une à l’autre.
        </p>
      </div>
      <div className="asso-hero__actions">
        {comparer}
        {gerer ? <button type="button" className="lc-btn lc-btn--primary" onClick={onNouvelle}><Plus size={16} aria-hidden="true" /> Nouvelle association</button> : null}
        <Link href={CHEMIN_REGLAGES} className="lc-btn lc-btn--ghost lc-btn--icon" aria-label="Réglages des associations" title="Réglages Soirées › Associations">
          <Settings2 size={16} aria-hidden="true" />
        </Link>
      </div>
    </header>
  )
}

function lireAdresse(): { filtres: FiltresListe; vue: string | null } {
  const p = new URLSearchParams(window.location.search)
  return { filtres: filtresDepuisUrl(p), vue: p.get('vue') === VUE_A_DECIDER ? VUE_A_DECIDER : null }
}

/** Le cas visé par l'alerte « avant la nuit » (`&cas=pretix|2026-10-15`), lu une fois à l'arrivée. */
function casDeLAdresse(): string | null {
  const c = new URLSearchParams(window.location.search).get('cas')
  return c && /^[a-z]+\|\d{4}-\d{2}-\d{2}$/.test(c) ? c : null
}

function Liste({ r }: { r: ReponseListe }) {
  const [{ filtres, vue }, setAdresse] = useState(lireAdresse)
  const setFiltres = (f: FiltresListe) => setAdresse((a) => ({ ...a, filtres: f }))
  const [saisie, setSaisie] = useState(filtres.q)
  const [selection, setSelection] = useState<string[]>([])
  const [nouvelle, setNouvelle] = useState(false)

  useEffect(() => { remplacerRecherche(CHEMIN_LISTE, filtresVersUrl(filtres, vue)) }, [filtres, vue])
  // Recherche temporisée : l'adresse suit la pause de frappe, pas chaque touche.
  useEffect(() => {
    const t = window.setTimeout(() => setAdresse((a) => (a.filtres.q === saisie ? a : { ...a, filtres: { ...a.filtres, q: saisie } })), 250)
    return () => window.clearTimeout(t)
  }, [saisie])
  // Arrivée par une notification : la page défile UNE FOIS jusqu'à « À décider »,
  // puis `vue` quitte l'adresse — sinon le Retour depuis une fiche ramenait la
  // page à « À décider » au lieu de l'endroit où l'on était dans la liste.
  // Arrivée par l'alerte « avant la nuit » : jusqu'au CAS lui-même, une fois
  // sa carte affichée (la liste « À rattacher » se lit après la page : on
  // la guette 3 secondes au plus, sinon « À décider » suffit).
  const [cas] = useState(casDeLAdresse)
  useEffect(() => {
    if (vue !== VUE_A_DECIDER) return
    let essais = 0
    let t = 0
    const viser = () => {
      const carte = cas ? document.getElementById(idDuCas(cas)) : null
      if (!carte && cas && essais++ < 12) { t = window.setTimeout(viser, 250); return }
      ;(carte ?? document.getElementById('a-decider'))?.scrollIntoView({ block: carte ? 'center' : 'start', behavior: 'smooth' })
      carte?.classList.add('so-ratt--vise')
      setAdresse((a) => ({ ...a, vue: null }))
    }
    t = window.setTimeout(viser, 120)
    return () => window.clearTimeout(t)
  }, [vue, cas])

  const lignes = useMemo(() => appliquerFiltres(r.associations, filtres), [r.associations, filtres])
  const saisons = useMemo(() => saisonsDe(r.associations), [r.associations])
  const actives = r.associations.filter((a) => a.status === 'ACTIVE')
  const prochaine = actives.filter((a) => a.indicateurs.prochaine_nuit)
    .sort((a, b) => a.indicateurs.prochaine_nuit!.localeCompare(b.indicateurs.prochaine_nuit!))[0] ?? null
  // Les totaux viennent du serveur, chaque nuit et chaque billet une fois :
  // additionner les lignes recomptait une nuit commune (Intersanté le 09/09 :
  // 979 billets sous chacun des membres ET sous le groupement).
  const t = r.totaux

  const comparer = (
    <button type="button" className="lc-btn" disabled={selection.length < COMPARER_MIN} onClick={() => navigate(cheminComparer(selection))}
      title={selection.length < COMPARER_MIN ? `Cochez de ${COMPARER_MIN} à ${COMPARER_MAX} associations dans la liste` : undefined}>
      <GitCompareArrows size={16} aria-hidden="true" /> Comparer{selection.length > 0 ? ` (${selection.length})` : ''}
    </button>
  )

  return (
    <>
      <EnTete gerer={r.droits.gerer} onNouvelle={() => setNouvelle(true)} comparer={comparer} />

      <section className="asso-kpis" aria-label="En un regard">
        <div className="asso-kpi"><span className="asso-kpi__libelle">Associations actives</span><span className="asso-kpi__valeur">{nombre(actives.length)}</span></div>
        <div className="asso-kpi"><span className="asso-kpi__libelle">Nuits suivies</span><span className="asso-kpi__valeur">{nombre(t.nuits)}</span>
          <span className="asso-kpi__aide">chaque nuit une fois, même partagée par plusieurs associations</span></div>
        <div className="asso-kpi"><span className="asso-kpi__libelle">Billets vendus</span><span className="asso-kpi__valeur">{nombre(t.vendus_total)}</span>
          <span className="asso-kpi__aide">chaque billet une fois, groupements compris</span></div>
        <div className="asso-kpi"><span className="asso-kpi__libelle">Entrées</span><span className="asso-kpi__valeur">{nombre(t.entrees_total)}</span>
          <span className="asso-kpi__aide">{aideEntrees(t)}</span></div>
        <div className="asso-kpi">
          <span className="asso-kpi__libelle">Prochaine soirée</span>
          {prochaine ? (
            <>
              <Link href={cheminFiche(prochaine.id)} className="asso-kpi__valeur asso-kpi__valeur--texte">{prochaine.name}</Link>
              <span className="asso-kpi__aide">{nuitLongue(prochaine.indicateurs.prochaine_nuit!)}</span>
            </>
          ) : <span className="asso-kpi__valeur asso-kpi__valeur--texte asso-kpi__valeur--muet">Aucune annoncée</span>}
        </div>
        <a href="#a-decider" className={`asso-kpi asso-kpi--lien${r.a_decider.total > 0 ? ' asso-kpi--attention' : ''}`}
          onClick={(e) => { e.preventDefault(); document.getElementById('a-decider')?.scrollIntoView({ block: 'start', behavior: 'smooth' }) }}>
          <span className="asso-kpi__libelle">À décider</span>
          <span className="asso-kpi__valeur">{nombre(r.a_decider.total)}</span>
          <span className="asso-kpi__aide">{detailADecider(r.a_decider)}</span>
        </a>
      </section>

      <ADecider associations={r.associations} />

      <section className="lc-panel gd-panel asso-bloc" aria-labelledby="asso-table-titre">
        <header className="lc-panel__head asso-bloc__tete">
          <h2 className="lc-section-title" id="asso-table-titre">Les associations</h2>
          <span className="lc-hint">{compte(lignes.length, 'association', 'associations')}{selection.length > 0 ? ` · ${selection.length} à comparer` : ''}</span>
        </header>
        <div className="lc-panel__body">
          <div className="asso-filtres" role="search" aria-label="Filtres de la liste">
            <label className="lc-search asso-filtres__recherche">
              <Search size={16} className="lc-search__icon" aria-hidden="true" />
              <input type="search" value={saisie} onChange={(e) => setSaisie(e.target.value)} maxLength={80}
                placeholder="Nom, école, alias…" aria-label="Rechercher une association" />
            </label>
            <SegmentedControl<FiltreStatut> value={filtres.statut} ariaLabel="Statut des associations" size="sm"
              options={[{ value: 'actives', label: 'Actives' }, { value: 'archivees', label: 'Archivées' }, { value: 'toutes', label: 'Toutes' }]}
              onChange={(v) => setFiltres({ ...filtres, statut: v })} />
            <Select compact label="Type" value={filtres.kind} ariaLabel="Filtrer par type"
              options={[{ value: '', label: 'Tous' }, ...(['BDE', 'ASSO', 'GROUPEMENT'] as const).map((k) => ({ value: k, label: LIBELLES_KIND[k] }))]}
              onChange={(v) => setFiltres({ ...filtres, kind: v as FiltresListe['kind'] })} />
            <Select compact label="Saison" value={filtres.saison} ariaLabel="Filtrer par saison"
              options={[{ value: '', label: 'Toutes' }, ...saisons.map((s) => ({ value: s, label: s.replace('-', ' – ') }))]}
              onChange={(v) => setFiltres({ ...filtres, saison: v })} />
            <Select compact label="Ordre" value={filtres.tri} ariaLabel="Ordre de la liste"
              options={[{ value: 'prochaine', label: 'Prochaine soirée' }, { value: 'derniere', label: 'Dernière soirée' }, { value: 'vendus', label: 'Billets vendus' }, { value: 'nom', label: 'Nom' }]}
              onChange={(v) => setFiltres({ ...filtres, tri: v as TriListe })} />
            {filtresActifs(filtres) ? (
              <button type="button" className="lc-btn lc-btn--sm lc-btn--ghost" onClick={() => { setSaisie(''); setFiltres({ ...FILTRES_DEFAUT, tri: filtres.tri }) }}>
                <RotateCcw size={14} aria-hidden="true" /> Réinitialiser
              </button>
            ) : null}
          </div>

          {lignes.length === 0 ? (
            <div className="asso-vide">
              <p className="asso-vide__titre">{filtresActifs(filtres) ? 'Aucune association ne correspond à ces filtres' : 'Aucune association confirmée'}</p>
              <p className="lc-hint">
                {filtresActifs(filtres) ? 'Élargissez la recherche ou réinitialisez les filtres.'
                  : r.a_decider.suggestions > 0 ? `Gestion en suggère ${r.a_decider.suggestions} dans « À décider », plus haut : confirmez celles qui existent.`
                    : 'Créez la première avec « Nouvelle association ».'}
              </p>
            </div>
          ) : (
            <div className="asso-liste">
              <Table lignes={lignes} selection={selection} onSelection={setSelection} />
              <Cartes lignes={lignes} selection={selection} onSelection={setSelection} />
            </div>
          )}
          {selection.length >= COMPARER_MAX ? <p className="lc-hint" role="status">{COMPARER_MAX} associations au plus dans une comparaison.</p> : null}
        </div>
      </section>

      <NuitsDesEvenementsBloc />

      <ModaleNouvelle open={nouvelle} onClose={() => setNouvelle(false)} associations={r.associations} />
    </>
  )
}

/* ═══════════════════════════════════════ table et cartes ═══ */

function Tendance({ l }: { l: LigneAssociation }) {
  const t = l.indicateurs.tendance
  if (!t) return <span className="lc-hint">—</span>
  const Icone = t.sens === 'HAUSSE' ? ArrowUpRight : t.sens === 'BAISSE' ? ArrowDownRight : ArrowRight
  return (
    <span className={`asso-tendance asso-tendance--${t.sens.toLowerCase()}`} title={`La dernière soirée face à la moyenne des ${t.serie.length - 1} précédentes`}>
      <Icone size={14} aria-hidden="true" /> {texteTendance(t)}
    </span>
  )
}

interface PropsListe { lignes: LigneAssociation[]; selection: string[]; onSelection: (s: string[]) => void }

function CaseComparer({ l, selection, onSelection }: { l: LigneAssociation } & Omit<PropsListe, 'lignes'>) {
  const coche = selection.includes(l.id)
  return (
    <Checkbox checked={coche} disabled={!coche && selection.length >= COMPARER_MAX}
      ariaLabel={`${coche ? 'Retirer' : 'Ajouter'} ${l.name} ${coche ? 'de' : 'à'} la comparaison`}
      onChange={() => onSelection(basculerSelection(selection, l.id))} />
  )
}

function Table({ lignes, selection, onSelection }: PropsListe) {
  return (
    <table className="lc-table asso-table">
      <thead>
        <tr>
          <th scope="col" className="asso-table__case"><span className="asso-masque">Comparer</span></th>
          <th scope="col">Association</th>
          <th scope="col">Soirées</th>
          <th scope="col">Dernière</th>
          <th scope="col">Prochaine</th>
          <th scope="col" className="asso-num">Vendus</th>
          <th scope="col" className="asso-num">Entrées</th>
          <th scope="col" className="asso-num">Par soirée</th>
          <th scope="col" className="asso-num" title="Billets vendus jamais scannés, sur les soirées finies dont les scans sont relevés">Absents</th>
          <th scope="col">Tendance</th>
          <th scope="col">Sources</th>
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => (
          <tr key={l.id}>
            <td className="asso-table__case"><CaseComparer l={l} selection={selection} onSelection={onSelection} /></td>
            <td>
              <Link href={cheminFiche(l.id)} className="asso-table__nom">{l.name}</Link>
              <span className="asso-table__sous">
                <PastilleKind kind={l.kind} /> <PastilleStatut statut={l.status} />
                {l.member_of_name ? <span className="lc-hint">{l.member_of_name}</span> : null}
                {l.kind === 'GROUPEMENT' && l.membres > 0 ? <span className="lc-hint">{compte(l.membres, 'membre', 'membres')}</span> : null}
              </span>
            </td>
            <td>{nombre(l.indicateurs.nuits)}</td>
            <td className="asso-date">{l.indicateurs.derniere_nuit ? <span title={nuitLongue(l.indicateurs.derniere_nuit)}>{nuitCourte(l.indicateurs.derniere_nuit)}</span> : '—'}</td>
            <td className="asso-date">{l.indicateurs.prochaine_nuit ? <strong title={nuitLongue(l.indicateurs.prochaine_nuit)}>{nuitCourte(l.indicateurs.prochaine_nuit)}</strong> : '—'}</td>
            <td className="asso-num">{nombre(l.indicateurs.vendus_total)}</td>
            <td className="asso-num">{nombre(l.indicateurs.entrees_total)}{l.indicateurs.nuits_sans_entrees > 0 || l.indicateurs.nuits_entrees_saisies > 0 ? <span className="asso-renvoi" title={aideEntrees(l.indicateurs)}>*</span> : null}</td>
            <td className="asso-num">{nombre(l.indicateurs.moyenne_vendus)}</td>
            <td className="asso-num">{pourcent(l.indicateurs.no_show_pct)}</td>
            <td><Tendance l={l} /></td>
            <td>{l.indicateurs.sources.map(libelleSource).join(', ') || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Cartes({ lignes, selection, onSelection }: PropsListe) {
  return (
    <ul className="asso-cartes-liste">
      {lignes.map((l) => (
        <li key={l.id} className="asso-ligne-carte">
          <div className="asso-ligne-carte__tete">
            <CaseComparer l={l} selection={selection} onSelection={onSelection} />
            <Link href={cheminFiche(l.id)} className="asso-table__nom">{l.name}</Link>
            <PastilleKind kind={l.kind} /> <PastilleStatut statut={l.status} />
          </div>
          <dl className="asso-ligne-carte__faits">
            <div><dt>Soirées</dt><dd>{nombre(l.indicateurs.nuits)}</dd></div>
            <div><dt>Vendus</dt><dd>{nombre(l.indicateurs.vendus_total)}</dd></div>
            <div><dt>Entrées</dt><dd>{nombre(l.indicateurs.entrees_total)}{l.indicateurs.nuits_sans_entrees > 0 || l.indicateurs.nuits_entrees_saisies > 0 ? <span className="asso-renvoi" title={aideEntrees(l.indicateurs)}>*</span> : null}</dd></div>
            <div><dt>Absents</dt><dd>{pourcent(l.indicateurs.no_show_pct)}</dd></div>
            <div><dt>Dernière</dt><dd>{l.indicateurs.derniere_nuit ? nuitLongue(l.indicateurs.derniere_nuit) : '—'}</dd></div>
            <div><dt>Prochaine</dt><dd>{l.indicateurs.prochaine_nuit ? nuitLongue(l.indicateurs.prochaine_nuit) : '—'}</dd></div>
          </dl>
          <Tendance l={l} />
        </li>
      ))}
    </ul>
  )
}
